<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card" style="margin-top: 50px">
        <Header :title="`Approval TNA ${upperCase(aboutMe?.roles) ?? ''}`" />
        <div class="card-body">
          <div class="approval-hero mb-4">
            <div class="approval-filter-panel">
              <div class="approval-filter-header">
                <div>
                  <h5 class="mb-1">Approval Scope</h5>
                  <small class="text-muted">Review pengajuan TNA karyawan sesuai periode yang dipilih.</small>
                </div>
              </div>

              <div class="approval-filter-grid">
                <div class="approval-control">
                  <label>TNA Period</label>
                  <v-select
                    :options="optionsSelectPeriod"
                    :value="optionsSelectPeriod.find((option) => option.value === selectedPeriod) || null"
                    :reduce="(option) => option.value"
                    label="text"
                    placeholder="-- Select a period --"
                    :clearable="false"
                    @input="selectedPeriod = $event"
                  />
                </div>
              </div>
            </div>

            <div class="approval-info-grid">
              <EmpCard :emp="emp" :isLoadingEmp="isLoadingEmp || isLoading" compact />
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="$router.push('/tna/tna-report')">
                <i class="fe fe-arrow-left mr-1"></i>
                Back to list
              </button>
            </div>
          </div>
          <Summary
            :isLoadingSummary="isLoadingSummary"
            :summaryData="summaryData"
          />
          <InputSearch
            v-model="searchQuery"
            placeholder="Search by training title..."
            @enter="runSearch"
          />
          <div v-if="isLoading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Loading TNA detail data...</p>
          </div>
          <!-- Empty State -->
          <div v-else-if="!data || data.length === 0" class="text-center py-5">
            <i
              class="fe fe-inbox text-muted"
              style="font-size: 48px; opacity: 0.6"
            ></i>
            <p class="mt-3 text-muted">Tidak ada data untuk ditampilkan.</p>
          </div>
          <div v-else class="tna-kanban mb-4">
            <div class="kanban-board">
              <div
                v-for="column in kanbanColumns"
                :key="column.year"
                class="kanban-column"
              >
                <div
                  class="kanban-column-header d-flex align-items-center justify-content-between"
                >
                  <div class="d-flex align-items-center">
                    <span class="year-badge mr-2">{{ column.year }}</span>
                    <span class="badge badge-secondary"
                      >{{ column.items.length }} training</span
                    >
                  </div>
                </div>

                <draggable
                  :list="column.items"
                  group="tna-report-year"
                  ghost-class="card-ghost"
                  chosen-class="card-chosen"
                  drag-class="card-drag"
                  animation="200"
                  class="kanban-card-list"
                  :empty-insert-threshold="40"
                  @end="handleKanbanDragEnd"
                  :filter="'.kanban-empty-drop'"
                >
                  <div
                    v-for="item in column.items"
                    :key="item.id"
                    class="kanban-card drag-handle"
                  >
                    <div class="kanban-card-inner">
                      <div class="card-main-row">
                        <input
                          v-if="isItemSelectable(item)"
                          type="checkbox"
                          class="form-check-input cursor-pointer kanban-check-input"
                          :checked="isItemChecked(item)"
                          @change="() => toggleSelect(item)"
                        />
                        <div class="card-content-wrap">
                          <div class="card-title-line">
                            <p class="card-title-text">
                              {{ getCardTitle(item) }}
                            </p>
                            <b-button
                              variant="outline-primary"
                              size="sm"
                              class="card-eye-btn"
                              @click="openTrainingDetail(item)"
                            >
                              <i class="fe fe-eye"></i>
                            </b-button>
                          </div>
                          <div class="card-meta-row">
                            <span :class="getLearningTypeClass(item)">
                              <i
                                :class="
                                  isMandatory(item)
                                    ? 'fe fe-lock mr-1'
                                    : 'fe fe-settings mr-1'
                                "
                              ></i>
                              {{ getLearningTypeLabel(item) }}
                            </span>
                            <b-badge
                              :variant="getBadgeStatus(item.status)"
                              :class="{
                                'badge-light-readable':
                                  getBadgeStatus(item.status) === 'light',
                              }"
                            >
                              {{ getApprovalStatusLabel(item.status) }}
                            </b-badge>
                          </div>
                          <div class="card-price-text">
                            {{
                              item.training?.currency &&
                              item.training?.price !== null &&
                              item.training?.price !== undefined
                                ? formatCurrency(
                                    item.training.currency,
                                    item.training.price
                                  )
                                : "-"
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="column.items.length === 0"
                    slot="footer"
                    class="kanban-empty-drop"
                  >
                    <i class="fe fe-inbox"></i>
                    <p class="mb-0">Drop training di sini</p>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
          <!-- <b-pagination
            v-model="payload.page"
            :total-rows="totalItems"
            :per-page="payload.perPage"
            @change="handlePageChange"
            align="right"
            class="mt-3"
          /> -->
          <div
            v-if="activeTab !== 'experiential'"
            class="d-flex justify-content-end mt-4"
          >
            <b-button
              v-if="activeTab === 'social' || activeTab === 'structured'"
              class="mr-2"
              :disabled="selectedItems.length === 0"
              @click="openRejectModal"
            >
              Reject ({{ selectedItems.length }})
            </b-button>
            <b-button
              variant="primary"
              :disabled="selectedItems.length === 0"
              @click="openSubmitModal"
            >
              Submit ({{ selectedItems.length }})
            </b-button>
          </div>
        </div>
        <b-modal
          id="modal-confirmation"
          title="Konfirmasi Item Terpilih"
          @ok="handleSubmit"
        >
          <div v-if="selectedItems.length === 0">
            Tidak ada item yang dipilih.
          </div>

          <div v-else>
            <p class="mb-3">
              <strong
                >Apakah Anda yakin ingin approve {{ selectedItems.length }} item
                berikut?</strong
              >
            </p>
            <div class="border rounded p-3 bg-light">
              <ol class="mb-0 pl-3">
                <li
                  v-for="(item, idx) in selectedItems"
                  :key="idx"
                  class="mb-2"
                >
                  {{ item?.training?.title || item?.coaching?.title }}
                  <small class="d-block text-muted">
                    Next step: {{ getNextApproverLabel(item) }}
                  </small>
                </li>
              </ol>
            </div>
          </div>
        </b-modal>
        <b-modal
          id="modal-reject-confirmation"
          title="Konfirmasi Tolak Item"
          @ok="handleRejectSelected"
          ok-variant="danger"
          ok-title="Tolak"
        >
          <div v-if="selectedItems.length === 0">
            Tidak ada item yang dipilih.
          </div>

          <div v-else>
            <p class="mb-3">
              <strong
                >Apakah Anda yakin ingin menolak {{ selectedItems.length }} item
                berikut?</strong
              >
            </p>
            <div class="border rounded p-3 bg-light">
              <ol class="mb-0 pl-3">
                <li
                  v-for="(item, idx) in selectedItems"
                  :key="idx"
                  class="mb-2"
                >
                  {{ item?.training?.title || item?.coaching?.title }}
                </li>
              </ol>
            </div>
          </div>
        </b-modal>
        <DetailTNA
          :showDetailModal="showDetailTrainingModal"
          :isLoadingModal="isLoadingDetailTraining"
          :selectedTNA="selectedTraining"
          @close="closeTrainingDetailModal"
        />
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import vSelect from "vue-select";
import EmpCard from "../../components/emp-card.vue";
import Header from "../../components/header.vue";
import Summary from "../../components/summary.vue";
import InputSearch from "../../components/input-search.vue";
import DetailTNA from "../../modal/DetailTNA.vue";
import tnaReportDetailLogic from "./tna-report-detail.logic";

export default {
  name: "TnaReportDetail",
  components: {
    draggable,
    vSelect,
    Header,
    EmpCard,
    Summary,
    InputSearch,
    DetailTNA,
  },
  mixins: [tnaReportDetailLogic],
};
</script>

<style scoped>
.approval-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 0.9fr);
  gap: 16px;
}

.approval-filter-panel {
  border: 1px solid #e9edf5;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.04);
  padding: 16px;
}

.approval-filter-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.approval-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 14px;
}

.approval-control label {
  display: block;
  color: #4b587c;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.approval-info-grid {
  display: grid;
  gap: 12px;
}

.kanban-board {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  overflow: hidden;
  padding-bottom: 0.15rem;
  align-items: stretch;
}

.kanban-column {
  flex: 1 1 calc((100% - 3rem) / 5);
  min-width: 0;
  background: #f9fafc;
  border-radius: 8px;
  border: 1px solid #e5eaf2;
  overflow: hidden;
}

@media (max-width: 768px) {
  .kanban-column {
    flex-basis: calc((100% - 1rem) / 2);
  }
}

@media (max-width: 1200px) {
  .approval-filter-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 992px) {
  .approval-hero,
  .approval-filter-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .kanban-column {
    flex-basis: 100%;
  }
}

.kanban-column-header {
  padding: 0.55rem 0.75rem;
  background: #fff;
  border-bottom: 1px solid #e5eaf2;
}

.year-badge {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2c3e50;
}

.badge-light-readable {
  color: #212529 !important;
  background-color: #f1f3f5 !important;
  border: 1px solid #dee2e6;
}

.kanban-card-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.55rem;
  min-height: 82px;
}

.kanban-card {
  width: 100%;
  background: #fff;
  border-radius: 7px;
  border: 1px solid #dfe5ef;
  box-shadow: 0 1px 2px rgba(30, 41, 59, 0.06);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.kanban-card:hover {
  box-shadow: 0 6px 14px rgba(30, 41, 59, 0.12);
  transform: translateY(-1px);
}

.kanban-card-inner {
  padding: 0.55rem 0.6rem;
}

.card-main-row {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  min-width: 0;
}

.card-title-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 26px;
  align-items: start;
  gap: 0.4rem;
}

.card-title-text {
  font-size: 0.82rem;
  font-weight: 600;
  color: #343a40;
  line-height: 1.22;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-content-wrap,
.card-content {
  flex: 1;
  min-width: 0;
}

.card-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem 0.4rem;
  margin-top: 0.28rem;
}

.card-price-text {
  color: #7a8bab;
  font-size: 0.73rem;
  line-height: 1.1;
  margin-top: 0.25rem;
}

.card-eye-btn {
  width: 26px;
  height: 26px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.mandatory-label {
  font-size: 0.7rem;
  color: #f59e0b;
  font-weight: 600;
  line-height: 1;
}

.dev-label {
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
}

.kanban-empty-drop {
  min-height: 56px;
  width: 100%;
  border: 1px dashed #c6d1df;
  border-radius: 7px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #a5b2c5;
  font-size: 0.76rem;
  gap: 6px;
  background: rgba(255, 255, 255, 0.45);
}

.card-ghost {
  opacity: 0.4;
  background: #cfe2ff;
  border: 2px dashed #0d6efd;
}

.card-chosen {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.card-drag {
  transform: rotate(2deg);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
}

.drag-handle {
  cursor: grab;
}

.kanban-check-input {
  margin-left: 0 !important;
  position: static;
  flex-shrink: 0;
  margin-top: 0.18rem;
}
</style>
